import { getIconClassName } from '@uifabric/styling/lib/index';
import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Toggle } from 'office-ui-fabric-react/lib/components/Toggle';
import { ControlIcons } from 'owa-control-icons';
import { format } from 'owa-strings';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { OptionsCheckbox } from 'owa-options-controls';

const styles = require('./RuleRow.scss');
let classNames = require('classnames/bind').bind(styles);

export interface RuleRowProps {
    ruleName: string;
    ruleDescription?: string;
    isChecked: boolean;
    isDisabled?: boolean;
    enableDisableRule: (shouldBeEnabled: boolean) => void;
    removeRule: () => void;
    editRule?: () => void;
    moveRule?: (shouldMoveUp: boolean) => void;
    disableUpArrow?: boolean;
    disableDownArrow?: boolean;
    runRuleNow?: () => void;
    disableRunRuleNow?: boolean;
    isRuleRunning?: boolean;
    alertString?: string;
    customClassNames?: any;
    onDoubleClick?: () => void;
    isRetentionRule?: boolean; // indicates whether this row is a retention policy
    showCheckbox?: boolean; // indicates if the checkbox should be shown or not
    hideDelete?: boolean; // indicates whether the delete icon should be hidden
}

@observer
export default class RuleRow extends React.Component<RuleRowProps, {}> {
    public static defaultProps = {
        isRetentionRule: false,
        showCheckbox: false,
        hideDelete: false,
    };

    render() {
        const {
            ruleName,
            ruleDescription,
            isChecked,
            isDisabled,
            editRule,
            moveRule,
            alertString,
            customClassNames,
            isRetentionRule,
            showCheckbox,
            hideDelete,
        } = this.props;
        const isRuleChecked = !isDisabled ? isChecked : false;
        let buttonStyles = {
            rootDisabled: styles.iconDisabled,
        };

        return (
            <div className={classNames(styles.ruleContainer, customClassNames)} tabIndex={-1}>
                <div className={styles.ruleLeft}>
                    {!isRetentionRule && (
                        <Toggle
                            className={styles.ruleToggle}
                            checked={isChecked && !isDisabled}
                            disabled={isDisabled}
                            ariaLabel={strings.options_rules_enableRuleAriaText}
                            title={
                                isChecked
                                    ? strings.options_rules_disableRuleToolTipText
                                    : strings.options_rules_enableRuleToolTipText
                            }
                            onClick={evt => {
                                this.onEnableDisableRule(evt, !isRuleChecked);
                            }}
                        />
                    )}
                    {showCheckbox && (
                        <OptionsCheckbox
                            className={styles.ruleCheckbox}
                            defaultChecked={isChecked}
                            onChange={this.onEnableDisableRule}
                            title={
                                isChecked
                                    ? strings.options_retentionPolicies_disablePolicyToolTipText
                                    : strings.options_retentionPolicies_enablePolicyToolTipText
                            }
                            ariaLabel={ruleName}
                        />
                    )}

                    <div
                        className={classNames(
                            styles.ruleText,
                            !isRetentionRule && !isRuleChecked && styles.disabledRuleText
                        )}
                        onDoubleClick={this.props.onDoubleClick}>
                        <div className={styles.ruleName}>{ruleName} </div>
                        {ruleDescription && (
                            <div className={styles.ruleHelperText}>{ruleDescription}</div>
                        )}
                        {alertString && ( // Show alert text if there is a problem with the rule
                            <div className={styles.ruleAlert}>
                                <div
                                    className={classNames(
                                        getIconClassName(ControlIcons.Warning),
                                        styles.icon
                                    )}
                                />
                                <span>{alertString}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.icons}>
                    {this.props.runRuleNow &&
                        (this.props.isRuleRunning ? (
                            <Spinner
                                size={SpinnerSize.small}
                                className={styles.icon}
                                title={strings.options_rules_runningRuleSpinnerToolTipText}
                                aria-label={format(
                                    strings.options_rules_runningRuleSpinnerAriaText,
                                    ruleName
                                )}
                            />
                        ) : (
                            <IconButton
                                iconProps={{ iconName: ControlIcons.Play }}
                                disabled={this.props.disableRunRuleNow}
                                onClick={this.props.runRuleNow}
                                aria-label={format(
                                    strings.options_rules_runRuleNowAriaText,
                                    ruleName
                                )}
                                title={
                                    this.props.disableRunRuleNow
                                        ? strings.options_rules_runRuleNowDisabledToolTipText
                                        : strings.options_inboxRules_runRuleNow
                                }
                                styles={buttonStyles}
                            />
                        ))}
                    {moveRule && (
                        <IconButton
                            iconProps={{ iconName: ControlIcons.Up }}
                            disabled={this.props.disableUpArrow}
                            onClick={this.onMoveRuleUp}
                            aria-label={format(strings.options_rules_moveRuleUpAriaText, ruleName)}
                            title={strings.options_rules_moveRuleUpToolTipText}
                            styles={buttonStyles}
                        />
                    )}
                    {moveRule && (
                        <IconButton
                            iconProps={{ iconName: ControlIcons.Down }}
                            disabled={this.props.disableDownArrow}
                            onClick={this.onMoveRuleDown}
                            aria-label={format(
                                strings.options_rules_moveRuleDownAriaText,
                                ruleName
                            )}
                            title={strings.options_rules_moveRuleDownToolTipText}
                            styles={buttonStyles}
                        />
                    )}
                    {editRule && (
                        <IconButton
                            iconProps={{ iconName: ControlIcons.Edit }}
                            onClick={this.onEditRule}
                            aria-label={format(strings.options_rules_editRuleAriaText, ruleName)}
                            title={strings.options_rules_editRuleToolTipText}
                        />
                    )}
                    {!hideDelete && (
                        <IconButton
                            iconProps={{ iconName: ControlIcons.Delete }}
                            onClick={this.onRemoveRule}
                            aria-label={
                                isRetentionRule
                                    ? format(
                                          strings.options_retentionPolicies_deletePolicyAriaText,
                                          ruleName
                                      )
                                    : format(strings.options_rules_deleteRuleAriaText, ruleName)
                            }
                            title={
                                isRetentionRule
                                    ? strings.options_retentionPolicies_deletePolicyToolTipText
                                    : strings.options_rules_deleteRuleToolTipText
                            }
                        />
                    )}
                </div>
            </div>
        );
    }

    /**
     * Helper method that handles user click on toggle
     * @param event user click event
     * @param shouldBeEnabled whether rule should be enabled
     */
    private onEnableDisableRule = (
        event: React.MouseEvent<EventTarget> | React.FormEvent<HTMLElement | HTMLInputElement>,
        shouldBeEnabled: boolean
    ) => {
        event.stopPropagation();
        this.props.enableDisableRule(shouldBeEnabled);
    };

    /**
     * Helper method that handles user click on delete
     * @param event user click event
     */
    private onRemoveRule = (event: React.MouseEvent<EventTarget>) => {
        event.stopPropagation();
        this.props.removeRule();
    };

    /**
     * Helper method that handles user click on edit
     * @param event user click event
     */
    private onEditRule = (event: React.MouseEvent<EventTarget>) => {
        event.stopPropagation();
        this.props.editRule();
    };
    /**
     * Helper method that handles user click on edit
     * @param event user click event
     */
    private onMoveRuleUp = (event: React.MouseEvent<EventTarget>) => {
        event.stopPropagation();
        this.props.moveRule(true /* shouldMoveUp */);
    };
    /**
     * Helper method that handles user click on edit
     * @param event user click event
     */
    private onMoveRuleDown = (event: React.MouseEvent<EventTarget>) => {
        event.stopPropagation();
        this.props.moveRule(false /* shouldMoveUp */);
    };
}
